import React from 'react';
import '../../css/from.css';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Input, Button, Typography, Row, Col } from 'antd';
import { createContactContent, updateContactContent, selectContactSubmitStatus, resetSubmitStatus } from '../../redux/contactSlice';
import { PhoneOutlined, MailOutlined, FacebookOutlined, TwitterOutlined, InstagramOutlined, YoutubeOutlined, CheckCircleOutlined, PlusOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const ContactForm = ({ initialValues, onSuccess }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const submitStatus = useSelector(selectContactSubmitStatus);
  const isEditing = Boolean(initialValues?.id);

  React.useEffect(() => {
    form.resetFields();
    if (initialValues) {
      const formattedValues = {
        ...initialValues,
        socialMedia: initialValues.socialMedia ? (typeof initialValues.socialMedia === 'string' ? JSON.parse(initialValues.socialMedia) : initialValues.socialMedia) : {}
      };
      form.setFieldsValue(formattedValues);
    }
  }, [initialValues, form]);

  const onFinish = async (values) => {
    try {
      const dataToSubmit = {
        ...values,
        socialMedia: JSON.stringify(values.socialMedia || {})
      };

      if (isEditing) {
        await dispatch(updateContactContent({ id: initialValues.id, data: dataToSubmit })).unwrap();
        toast.success('Contact updated successfully');
      } else {
        await dispatch(createContactContent(dataToSubmit)).unwrap();
        toast.success('Contact created successfully');
      }
      onSuccess();
      dispatch(resetSubmitStatus());
    } catch (error) {
      console.error('Error:', error);
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} contact: ${error.message || error}`);
    }
  };

  return (
    <div className="about-form-wrapper">
      <div className="form-container">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
          initialValues={{
            email: '',
            phone: '',
            address: '',
            socialMedia: {},
            mapUrl: '',
          }}
        >
          <Row gutter={[15, 15]}>
            <Col xs={24}>
              <div className="premium-card card-indigo" style={{ marginBottom: '0' }}>
                <div className="card-header-tag">Primary</div>
                <Typography.Text className="section-label">Contact Details</Typography.Text>
                <Typography.Text className="section-description">Manage standard information for community inquiries.</Typography.Text>

                <Row gutter={[15, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="email"
                      label="Email Address"
                      rules={[
                        { required: true, message: 'Please enter email' },
                        { type: 'email', message: 'Please enter a valid email' }
                      ]}
                    >
                      <Input prefix={<MailOutlined />} placeholder="contact@example.com" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="phone"
                      label="Phone Number"
                      rules={[{ required: true, message: 'Please enter phone number' }]}
                    >
                      <Input prefix={<PhoneOutlined />} placeholder="+1 234 567 890" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="address"
                  label="Physical Address"
                  rules={[{ required: true, message: 'Please enter address' }]}
                  style={{ marginBottom: 0 }}
                >
                  <TextArea rows={4} placeholder="Main office address..." />
                </Form.Item>
              </div>
            </Col>

            <Col xs={24}>
              <Row gutter={[15, 0]} className="ant-row-stretch">
                <Col xs={24} lg={12}>
                  <div className="premium-card card-orange">
                    <div className="card-header-tag">Social</div>
                    <Typography.Text className="section-label">Social Media Handles</Typography.Text>
                    <Typography.Text className="section-description">Manage external profiles and presence.</Typography.Text>

                    <Form.Item name={['socialMedia', 'facebook']} label="Facebook">
                      <Input prefix={<FacebookOutlined />} placeholder="https://facebook.com/..." />
                    </Form.Item>

                    <Form.Item name={['socialMedia', 'twitter']} label="Twitter (X)">
                      <Input prefix={<TwitterOutlined />} placeholder="https://twitter.com/..." />
                    </Form.Item>

                    <Form.Item name={['socialMedia', 'instagram']} label="Instagram">
                      <Input prefix={<InstagramOutlined />} placeholder="https://instagram.com/..." />
                    </Form.Item>

                    <Form.Item name={['socialMedia', 'youtube']} label="YouTube" style={{ marginBottom: 0 }}>
                      <Input prefix={<YoutubeOutlined />} placeholder="https://youtube.com/..." />
                    </Form.Item>
                  </div>
                </Col>

                <Col xs={24} lg={12}>
                  <div className="premium-card card-blue">
                    <div className="card-header-tag">Map</div>
                    <Typography.Text className="section-label">Location Embed</Typography.Text>
                    <Typography.Text className="section-description">Provide a Google Maps iframe for the website.</Typography.Text>

                    <Form.Item
                      name="mapUrl"
                      label="Google Maps iframe snippet"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const v = String(value).trim();
                            return /<iframe[\s\S]*src=["']https?:\/\/(?:www\.)?google\.com\/maps[^"']*["'][\s\S]*><\/iframe>/i.test(v)
                              ? Promise.resolve()
                              : Promise.reject(new Error('Provide a valid iframe embed code.'));
                          },
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <TextArea
                        rows={10}
                        placeholder='<iframe src="https://www.google.com/maps/embed?..." ...></iframe>'
                      />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>

          <div className="sticky-action-footer">
            <Button type="default" size="large" onClick={() => onSuccess()} style={{ borderRadius: '8px', height: '44px' }}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={submitStatus === 'loading'}
              icon={isEditing ? <CheckCircleOutlined /> : <PlusOutlined />}
            >
              {isEditing ? 'Save changes' : 'Create contact'}
            </Button>
          </div>

          <div className="form-footer-spacer" />
        </Form>
      </div>
    </div>
  );
};

export default ContactForm;
